import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { hasIn } from 'lodash';
import { filter } from 'rxjs/operators';
import {
  Brand,
  EndUser,
  ConsumerIdentityParam,
  GetEndUserLinesParams,
} from 'src/app/core';
import { brand, brandUuid } from 'src/app/core/store/brands/brands.selectors';
import {
  actionEndUserGet,
  actionEndUserGetFailure,
  actionEndUserLinesGet,
} from 'src/app/dashboard/mobile/end-users/store/end-users.actions';
import {
  selectEndUser,
  selectEndUserIsLoading,
} from 'src/app/dashboard/mobile/end-users/store/end-users.selectors';
import { Location } from '@angular/common';
import { Actions, ofType } from '@ngrx/effects';

@UntilDestroy()
@Component({
  selector: 'app-search-bar-end-user',
  templateUrl: './search-bar-end-user.component.html',
  styleUrls: ['./search-bar-end-user.component.less'],
})
export class SearchBarEndUserComponent implements OnInit {
  hasError: boolean = false;
  END_USERS_PREFIX = 'PAGE.MOBILE.END_USERS';
  brandUuid: string;
  consumerIdKey = 'consumer-id';
  endUser: EndUser;
  consumerIdentityList = [
    {
      label: this.END_USERS_PREFIX + '.SEARCH.MSISDN',
      value: ConsumerIdentityParam.MSISDN,
    },
    {
      label: this.END_USERS_PREFIX + '.SEARCH.ICCID',
      value: ConsumerIdentityParam.ICCID,
    },
    {
      label: this.END_USERS_PREFIX + '.SEARCH.IMSI',
      value: ConsumerIdentityParam.IMSI,
    },
    {
      label: this.END_USERS_PREFIX + '.SEARCH.LINE_ID',
      value: ConsumerIdentityParam.LINE_ID,
    },
    {
      label: this.END_USERS_PREFIX + '.SEARCH.END_USER_ID',
      value: ConsumerIdentityParam.END_USER_ID,
    },
    {
      label: this.END_USERS_PREFIX + '.SEARCH.EMAIL',
      value: ConsumerIdentityParam.EMAIL,
    },
  ];
  searchBarPlaceHolders = {
    [ConsumerIdentityParam.ICCID]: '8952930000000000004F',
    [ConsumerIdentityParam.IMSI]: '334140000000000',
    [ConsumerIdentityParam.MSISDN]: '525550000',
    [ConsumerIdentityParam.LINE_ID]: 'a0ff1edc-2ee8-4fca-abd6-1a2e8f741186',
    [ConsumerIdentityParam.END_USER_ID]: 'abcdef01-2345-6789-0abc-def012345678',
    [ConsumerIdentityParam.CURP]: 'HEGG560427MVZRRL04',
    [ConsumerIdentityParam.EMAIL]: 'john.doe@email.com',
  };
  searchBarPlaceHolder: {} =
    this.searchBarPlaceHolders[ConsumerIdentityParam.MSISDN];
  loadingEndUser = false;
  consumerId: string;

  @Input() consumerIdentityParam: ConsumerIdentityParam =
    this.consumerIdentityList[0].value;
  @Input() hasRouteParam: Boolean = true;

  @Output() onUpdateConsumerId: EventEmitter<string> = new EventEmitter();
  @Output() onUpdateConsumerIdentityParam: EventEmitter<ConsumerIdentityParam> =
    new EventEmitter();
  @Output() onErrorChange: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private actions: Actions,
    private location: Location,
    private store: Store,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.store
      .pipe(select(selectEndUserIsLoading), untilDestroyed(this))
      .subscribe(
        (isLoadingEndUser) => (this.loadingEndUser = isLoadingEndUser)
      );

    this.store
      .pipe(select(brandUuid), untilDestroyed(this))
      .subscribe((brandUuid: string) => {
        this.brandUuid = brandUuid;
        this.fetchEndUser();
      });

    this.store
      .pipe(select(brand), untilDestroyed(this))
      .subscribe((brand: Brand) => {
        if (brand) {
          if (brand.isCurpEnabled) {
            this.consumerIdentityList.push({
              label: this.END_USERS_PREFIX + '.SEARCH.CURP',
              value: ConsumerIdentityParam.CURP,
            });
          } else {
            this.removeCURPDropdown();
          }
        } else {
          this.removeCURPDropdown();
        }
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(() => {
        if (this.hasRouteParam) {
          this.handleQueryNodes();
        }
      });
  }

  ngOnInit(): void {
    this.getEndUserState();
  }

  handleQueryNodes() {
    if (hasIn(this.route.snapshot.params, 'subscriberId')) {
      this.consumerId = this.route.snapshot.params['subscriberId'];
    }

    if (hasIn(this.route.snapshot.queryParams, this.consumerIdKey)) {
      this.consumerIdentityParam =
        this.route.snapshot.queryParams[this.consumerIdKey];
    } else {
      this.location.go(
        this.router.url,
        `${this.consumerIdKey}=${this.consumerIdentityParam}`
      );
    }

    this.updatePlaceholder(this.consumerIdentityParam);
  }

  removeCURPDropdown() {
    const curpIndex = this.consumerIdentityList.findIndex(
      (consumerIdentity) =>
        consumerIdentity.value === ConsumerIdentityParam.CURP
    );

    if (curpIndex !== -1) {
      this.consumerIdentityList.splice(curpIndex, 1);
    }
  }

  onConsumerIdChange(newConsumerId) {
    this.consumerId = newConsumerId.replace(/\s/g, '');
    this.onUpdateConsumerId.emit(newConsumerId);
  }

  private getEndUserState(): void {
    this.store
      .pipe(
        select(selectEndUser),
        filter((endUser) => !!endUser),
        untilDestroyed(this)
      )
      .subscribe((endUser: EndUser) => {
        this.endUser = endUser;
        this.hasError = false;
        this.onErrorChange.emit(false);

        const queryParams = {
          [this.consumerIdKey]: this.consumerIdentityParam,
        };

        this.dispatchEndUserLines();

        if (this.hasRouteParam) {
          if (hasIn(this.route.snapshot.params, 'subscriberId')) {
            this.router.navigateByUrl(
              this.router.url.replace(
                this.route.snapshot.params.subscriberId.replace(/\+/g, '%2B'),
                this.consumerId
              )
            );
          } else {
            if (this.consumerId) {
              this.router.navigate([`${this.consumerId}`], {
                relativeTo: this.route,
                queryParams,
                queryParamsHandling: 'merge',
              });
            }
          }
        }
      });

    this.actions
      .pipe(ofType(actionEndUserGetFailure), untilDestroyed(this))
      .subscribe(() => {
        this.hasError = true;
        this.onErrorChange.emit(true);
      });
  }

  fetchEndUser(): void {
    if (this.consumerIdentityParam && this.consumerId) {
      this.store.dispatch(
        actionEndUserGet({
          consumerIdentityParam: this.consumerIdentityParam,
          value: this.consumerId,
          brandId: this.brandUuid,
        })
      );
    }
  }

  updateConsumerIdentityParam(
    consumerIdentityParam: ConsumerIdentityParam
  ): void {
    const queryParam = {};
    queryParam[this.consumerIdKey] = consumerIdentityParam;
    this.updatePlaceholder(consumerIdentityParam);

    if (this.hasRouteParam) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: queryParam,
        queryParamsHandling: 'merge',
      });
    }

    this.onUpdateConsumerIdentityParam.emit(consumerIdentityParam);
  }

  updatePlaceholder(consumerIdentityParam: ConsumerIdentityParam) {
    this.searchBarPlaceHolder =
      this.searchBarPlaceHolders[consumerIdentityParam];
  }

  private dispatchEndUserLines() {
    const params: GetEndUserLinesParams = {
      endUserId: this.endUser.endUserId,
      optFields: ['brandId', 'groupId', 'activationTs'],
    };

    this.store.dispatch(actionEndUserLinesGet({ params }));
  }
}
